import React, { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";

const MAX_CHARACTER = 15;

// TODO: Tipar las props
export const NumericInput = (props: any) => {
  const {
    className,
    label,
    id,
    placeholder,
    middle,
    disabled,
    input,
    value,
    lastLabel,
    meta,
    allowDecimal = true,
    maxDecimal,
    maxInt,
    onChange,
    onBlur,
    showErrorMessage = true,
    testId,
  } = props;

  if (!allowDecimal && maxDecimal) {
    throw new Error(
      "The NumericInput component cant have maxDecimal if allowDecimal is false."
    );
  }

  const maxLength = useMemo(
    () => (allowDecimal ? maxInt + maxDecimal + 1 : maxInt),
    [allowDecimal, maxDecimal, maxInt]
  );

  const [internalValue, setInternalValue] = useState({
    internalValue: input ? input.value : "",
    isActive: false,
  });
  const lastValue = useRef("");

  const decimalRegex = useMemo(
    () =>
      new RegExp(
        `^[0-9]${maxInt ? `{0,${maxInt}}` : "*"}[.,]?[0-9]${
          maxDecimal ? `{0,${maxDecimal}}` : "*"
        }$`
      ),
    [maxDecimal, maxInt]
  );

  const validDecimal = useCallback(
    (valueInput: string) =>
      valueInput &&
      decimalRegex.test(valueInput) &&
      valueInput.length <= MAX_CHARACTER,
    [decimalRegex]
  );

  const handleNumberValidation = useCallback(
    (valueInput: string) => {
      let emitValue;
      if (allowDecimal && validDecimal(valueInput)) {
        const noComaValue = valueInput.replace(",", ".");
        emitValue = parseFloat(noComaValue);
      } else {
        if (/^\d+\d*$/.test(valueInput) && valueInput.length <= MAX_CHARACTER) {
          emitValue = parseFloat(valueInput);
        }
      }
      return emitValue ?? "";
    },
    [allowDecimal, validDecimal]
  );

  const format = useCallback(
    (value: string) => {
      const lastLen = lastValue.current.length;
      let formattedValue = value;
      if (allowDecimal) {
        formattedValue = formattedValue.replace(",", ".");
        if (
          formattedValue.length === maxInt &&
          lastLen !== maxInt + 1 &&
          !formattedValue.includes(".")
        ) {
          return `${formattedValue}.`;
        }
        if (
          formattedValue.length === maxInt + 1 &&
          lastLen === maxInt &&
          !formattedValue.includes(".")
        ) {
          const value1 = formattedValue.slice(0, maxInt);
          const value2 = formattedValue.slice(maxInt, maxInt + 1);
          return value1 + "." + value2;
        }
        if (
          formattedValue.length < lastLen &&
          lastValue.current.includes(".") &&
          !formattedValue.includes(".")
        ) {
          return formattedValue.substring(0, maxInt);
        }
      }
      return formattedValue;
    },
    [allowDecimal, maxInt]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checkedValue = handleNumberValidation(event.target.value);
      if (event.target.value === "" || typeof checkedValue === "number") {
        const value = event.target.value;
        const formattedValue = format(value);
        setInternalValue({
          internalValue: formattedValue,
          isActive: true,
        });
        lastValue.current = formattedValue;
        if (input) {
          input.onChange(
            isNaN(parseFloat(formattedValue)) ? "" : parseFloat(formattedValue)
          );
        }
        if (onChange) {
          onChange(
            isNaN(parseFloat(formattedValue)) ? "" : parseFloat(formattedValue)
          );
        }
      }
    },
    [handleNumberValidation, format, input, onChange]
  );

  const handleBlur = useCallback(() => {
    setInternalValue({
      internalValue: input ? input.value : value,
      isActive: false,
    });
    if (input) {
      input.onBlur();
    }
    if (onBlur) {
      onBlur();
    }
    if (lastValue.current.length === maxInt + 1) {
      lastValue.current = lastValue.current.substring(0, maxInt);
    }
  }, [input, maxInt, onBlur, value]);

  const handleFocus = useCallback(() => {
    setInternalValue({
      internalValue: input ? input.value : value,
      isActive: true,
    });
  }, [input, value]);

  const variableToShow = internalValue.isActive
    ? internalValue.internalValue.toString()
    : input.value;

  return (
    <div
      id={id}
      className={classNames({
        "has-error": meta.error && meta.touched,
      })}
    >
      <label
        id={id + "-label"}
        data-testId={testId + "-label"}
        htmlFor={id + "-input"}
        className={classNames(
          middle ? "label-Admin-Pickers-middle" : "label-Admin-Pickers",
          {
            labelError: meta.error && meta.touched,
            disabled: disabled,
            "last-label": lastLabel,
          }
        )}
      >
        {label}
      </label>
      <input
        id={id + "-input"}
        data-testId={testId}
        className={classNames(className, {
          disabled: disabled,
          inputError: meta.error && meta.touched,
        })}
        type={input.type}
        name={input.name}
        disabled={disabled}
        value={variableToShow}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        maxLength={maxLength}
        autoComplete="off"
      />
      {meta.error && meta.touched && showErrorMessage && (
        <div className="input-errors-container" id={id + "-error"}>
          <p className="errors" data-testId={testId + "-error"}>
            {meta.error}
          </p>
        </div>
      )}
    </div>
  );
};
